import React, { useState } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../utils/apiClient';
import { FaEnvelope, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import config from '../config';

const ForgotPassword = ({ onBackToLogin, isDarkMode }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      // NOTE: This endpoint needs to be provided by your backend
      const response = await apiClient.post(`${config.API_BASE_URL}/auth/password/reset`, { email });
      
      setMessage(
        "A password reset link has been sent to your email address. Please check your inbox (and spam folder)."
      );
      setEmail(''); // Clear input after success
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send reset link. Please try again or check your email address.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const c = {
    card: isDarkMode ? 'bg-gray-900/90 border-gray-700' : 'bg-white/95 border-gray-200',
    inputBg: isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900',
    text: isDarkMode ? 'text-gray-300' : 'text-gray-700',
    button: isDarkMode ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-700 text-white hover:bg-indigo-600'
  };

  return (
    <div className="flex justify-center items-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className={`w-full max-w-md p-8 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.15)] backdrop-blur ${c.card}`}
      >
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-100 dark:text-gray-900">
            <span
              className={`bg-clip-text text-transparent bg-gradient-to-r ${
                isDarkMode ? 'from-indigo-300 to-blue-400' : 'from-indigo-600 to-blue-700'
              }`}
            >
              Reset Password
            </span>
          </h2>
          <p className={`${c.text} mt-2 text-sm`}>
            Enter your email to receive a password reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {message ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center gap-3"
            >
              <FaCheckCircle className="text-xl" />
              <p className="text-sm font-medium">{message}</p>
            </motion.div>
          ) : (
            <>
              <div>
                <label className={`block text-sm font-medium mb-2 ${c.text}`}>Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all focus:ring-indigo-400 focus:border-indigo-400 ${c.inputBg}`}
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${c.button} ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg'}`}
              >
                {loading ? 'Sending link...' : 'Send Reset Link'}
              </motion.button>
            </>
          )}
        </form>

        <div className="text-center mt-6">
          <motion.button
            onClick={onBackToLogin}
            className={`flex items-center mx-auto text-sm gap-2 ${c.text} hover:text-indigo-500 transition-colors bg-transparent border-none p-0 cursor-pointer`}
            whileHover={{ x: -2 }}
          >
            <FaArrowLeft /> Back to Login
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
